import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TooltipHost, DirectionalHint } from "@fluentui/react";
import { toast } from "react-toastify";
import { FiChevronLeft, FiChevronRight, FiList, FiMaximize2, FiX, FiRefreshCw } from "react-icons/fi";

import JsonListTable_dash from "../JsonListTable/JsonListTable_dash";
import JsonListTableEditable_dash from "../JsonListTable/JsonListTableEditable_dash";
import { getBaseAiData, qRefreshLinkedData } from "../../api/qpi";
import { gfile, gloggedinuser, gselectedgroup } from "../../interfaces";
import { hidePdfExt_dash } from "./detailedMdls";
import styles from "./DashListSidebar_dash.module.css";
import { FiDatabase } from "react-icons/fi";

{/*
    DashListSidebar_dash
    Left pullable sidebar that shows data items (doc_category 'data') flagged with dash_list = 1
    as tabs; the active tab renders its synced json (invoicedata) below.
*/}

interface DashListSidebarProps {
    fileList: gfile[] | undefined;
    selectedGroup: gselectedgroup;
    loggedInUser: gloggedinuser;
    onOpenFull?: (file: gfile, data: any) => void;
    onRemove?: (file: gfile) => void;
}

// session cache of pulled data, keyed by fid + modified so a re-sync invalidates the entry
const dashDataCache_dash = new Map<string, any>();

const MIN_WIDTH = 320;
const DEF_WIDTH = 560;
const CLOSE_ANIM_MS = 200;   // keep in sync with dashSlideOut in DashListSidebar_dash.module.css

const openKey  = (oid: any) => `dashListOpen_${oid}`;
const tabKey   = (oid: any) => `dashListTab_${oid}`;
const widthKey = (oid: any) => `dashListWidth_${oid}`;

const filtersKey_dash = (uid: any, oid: any, fid: any) => `dashListFilters_${uid || "anon"}_${oid}_${fid}`;

const cacheKey_dash = (f: gfile) => `${f.fileid}_${f.modified || ""}_${f.ext_stamp || ""}`;

// list payload for data items is a placeholder ({} or {doc_category}) unless pulled explicitly
const isPlaceholderData_dash = (data: any): boolean => {
    if (data === null || data === undefined || data === "") return true;
    let d = data;
    if (typeof d === "string") {
        try { d = JSON.parse(d); } catch { return true; }
    }
    if (Array.isArray(d)) return false;
    if (typeof d !== "object") return true;
    const keys = Object.keys(d);
    return keys.length === 0 || (keys.length === 1 && keys[0] === "doc_category");
};

const toDate_dash = (v: any): Date | undefined => {
    if (!v) return undefined;
    const d = new Date(v);
    return isNaN(d.getTime()) ? undefined : d;
};

const DashListSidebar_dash: React.FC<DashListSidebarProps> = ({ fileList, selectedGroup, loggedInUser, onOpenFull, onRemove }) => {
    const oid = selectedGroup?.oid;

    const dashFiles = useMemo(
        () => (fileList || []).filter(f => f?.doc_category === "data" && f?.dash_list == 1).sort((a, b) => (a.name || "").localeCompare(b.name || "")),
        [fileList]
    );

    const [isOpen, setIsOpen]       = useState<boolean>(() => localStorage.getItem(openKey(oid)) === "1");
    const [activeFid, setActiveFid] = useState<string>(() => localStorage.getItem(tabKey(oid)) || "");
    const [width, setWidth]         = useState<number>(() => parseInt(localStorage.getItem(widthKey(oid)) || "", 10) || DEF_WIDTH);
    const [data, setData]           = useState<any>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const dragRef                   = useRef<{ startX: number; startW: number } | null>(null);
    const loadSeq                   = useRef(0);
    const [isClosing, setIsClosing] = useState(false);
    const closeTimer                = useRef<number | null>(null);
    const sidebarRef                = useRef<HTMLDivElement | null>(null);

    // re-read persisted ui state when switching groups
    useEffect(() => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        setIsClosing(false);
        setIsOpen(localStorage.getItem(openKey(oid)) === "1");
        setActiveFid(localStorage.getItem(tabKey(oid)) || "");
        setWidth(parseInt(localStorage.getItem(widthKey(oid)) || "", 10) || DEF_WIDTH);
    }, [oid]);

    const activeFile = useMemo(
        () => dashFiles.find(f => String(f.fileid) === String(activeFid)) || dashFiles[0],
        [dashFiles, activeFid]
    );

    // load active tab data (cached per fid + modified)
    useEffect(() => {
        if (!isOpen || !activeFile) return;
        const ck = cacheKey_dash(activeFile);
        if (dashDataCache_dash.has(ck)) {
            setData(dashDataCache_dash.get(ck));
            return;
        }
        if (!isPlaceholderData_dash(activeFile.invoicedata)) {
            const d = typeof activeFile.invoicedata === "string" ? JSON.parse(activeFile.invoicedata) : activeFile.invoicedata;
            dashDataCache_dash.set(ck, d);
            setData(d);
            return;
        }
        const seq = ++loadSeq.current;
        setIsLoading(true);
        setData(undefined);
        getBaseAiData(loggedInUser, selectedGroup, String(activeFile.fileid))
            .then(record => {
                if (seq !== loadSeq.current) return;
                const d = record?.invoicedata ?? [];
                dashDataCache_dash.set(ck, d);
                setData(d);
            })
            .catch(() => {
                if (seq === loadSeq.current) toast.error("Failed to load list data");
            })
            .finally(() => {
                if (seq === loadSeq.current) setIsLoading(false);
            });
    }, [isOpen, activeFile, loggedInUser, selectedGroup]);

    const openSidebar = useCallback(() => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        setIsClosing(false);
        setIsOpen(true);
        localStorage.setItem(openKey(oid), "1");
    }, [oid]);

    // play the slide-out animation, then unmount
    const closeSidebar = useCallback(() => {
        if (closeTimer.current) window.clearTimeout(closeTimer.current);
        localStorage.setItem(openKey(oid), "0");
        setIsClosing(true);
        closeTimer.current = window.setTimeout(() => {
            setIsOpen(false);
            setIsClosing(false);
        }, CLOSE_ANIM_MS);
    }, [oid]);

    useEffect(() => () => { if (closeTimer.current) window.clearTimeout(closeTimer.current); }, []);

    // close on outside click / Escape; clicks in Fluent layers (tooltips, callouts, modals, menus) are not "outside"
    useEffect(() => {
        if (!isOpen || isClosing) return;
        const isInLayer = (el: Element | null) => !!el?.closest?.(".ms-Layer, .Toastify");
        const onPointerDown = (ev: PointerEvent) => {
            const target = ev.target as Element | null;
            if (!target || sidebarRef.current?.contains(target) || isInLayer(target)) return;
            closeSidebar();
        };
        const onKeyDown = (ev: KeyboardEvent) => {
            if (ev.key !== "Escape" || ev.defaultPrevented) return;
            if (isInLayer(ev.target as Element | null) || document.querySelector(".ms-Modal")) return;   // let an open modal handle its own Escape
            closeSidebar();
        };
        document.addEventListener("pointerdown", onPointerDown, true);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("pointerdown", onPointerDown, true);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [isOpen, isClosing, closeSidebar]);

    const selectTab = useCallback((fid: string) => {
        localStorage.setItem(tabKey(oid), fid);
        setActiveFid(fid);
    }, [oid]);

    // persist the saved linked-data rows into the session cache + active view so edits survive tab switches
    const onLinkedDataPersisted = useCallback((newRows: any[]) => {
        if (!activeFile) return;
        dashDataCache_dash.set(cacheKey_dash(activeFile), newRows);
        setData(newRows);
    }, [activeFile]);

    // force-rebuild a tab's linked data (fileautomation.invoicedata) from the live PO table rows
    const [refreshingFid, setRefreshingFid] = useState<string>("");
    const refreshTabData = useCallback(async (f: gfile) => {
        const fidStr = String(f.fileid);
        setRefreshingFid(fidStr);
        try {
            const res = await qRefreshLinkedData(loggedInUser, selectedGroup?.selectionId || "", fidStr);
            if (res.status === "success") {
                const fresh = res.rows || [];
                dashDataCache_dash.set(cacheKey_dash(f), fresh);
                if (String(activeFile?.fileid) === fidStr) setData(fresh);
                toast.success(`Linked data refreshed (${res.count ?? 0} rows)`, { autoClose: 1500 });
            } else {
                toast.error(res.message ? `Refresh failed: ${res.message}` : "Failed to refresh linked data");
            }
        } catch {
            toast.error("Failed to refresh linked data");
        } finally {
            setRefreshingFid("");
        }
    }, [loggedInUser, selectedGroup, activeFile]);

    // drag the right edge to resize (sidebar is anchored left)
    const onResizeMove = useCallback((ev: MouseEvent) => {
        if (!dragRef.current) return;
        const maxW = Math.max(MIN_WIDTH, window.innerWidth - 80);
        const w = Math.min(maxW, Math.max(MIN_WIDTH, dragRef.current.startW + (ev.clientX - dragRef.current.startX)));
        setWidth(w);
    }, []);

    const onResizeEnd = useCallback(() => {
        dragRef.current = null;
        document.body.style.userSelect = "";
        window.removeEventListener("mousemove", onResizeMove);
        window.removeEventListener("mouseup", onResizeEnd);
        setWidth(w => {
            localStorage.setItem(widthKey(oid), String(Math.round(w)));
            return w;
        });
    }, [oid, onResizeMove]);

    const onResizeStart = useCallback((ev: React.MouseEvent) => {
        ev.preventDefault();
        dragRef.current = { startX: ev.clientX, startW: width };
        document.body.style.userSelect = "none";
        window.addEventListener("mousemove", onResizeMove);
        window.addEventListener("mouseup", onResizeEnd);
    }, [width, onResizeMove, onResizeEnd]);

    useEffect(() => () => {
        window.removeEventListener("mousemove", onResizeMove);
        window.removeEventListener("mouseup", onResizeEnd);
    }, [onResizeMove, onResizeEnd]);

    if (dashFiles.length === 0) return null;

    const canEdit = (selectedGroup?.permissionlevel || 0) >= 2;
    const canEditLinked = canEdit && (selectedGroup?.extractionfieldlist?.allow_linked_data_editing ?? 0) == 1;

    return (
        <div className={styles.dashAnchor}>
            {!isOpen && (
                <TooltipHost content={`Lists (${dashFiles.length})`} directionalHint={DirectionalHint.rightCenter}>
                    <div className={styles.pullHandle} onClick={openSidebar} role="button" aria-label="Open lists sidebar">
                        <FiDatabase size={12} />
                        <span className={styles.pullCount}>{dashFiles.length}</span>
                        <FiChevronRight size={12} />
                    </div>
                </TooltipHost>
            )}

            {isOpen && (
                <div ref={sidebarRef} className={`${styles.sidebar} ${isClosing ? styles.sidebarClosing : ""}`} style={{ width: `min(${width}px, 100vw)` }}>
                    <div className={styles.sidebarHead}>
                        <div className={styles.sidebarTitle}><FiList size={16} /> Lists</div>
                        <div className={styles.headActions}>
                            {activeFile && onOpenFull && (
                                <TooltipHost content="Open full view" directionalHint={DirectionalHint.bottomCenter}>
                                    <div className={styles.headBtn} onClick={() => onOpenFull(activeFile, data)}><FiMaximize2 size={15} /></div>
                                </TooltipHost>
                            )}
                            <TooltipHost content="Collapse" directionalHint={DirectionalHint.bottomCenter}>
                                <div className={styles.headBtn} onClick={closeSidebar}><FiChevronLeft size={18} /></div>
                            </TooltipHost>
                        </div>
                    </div>

                    <div className={styles.tabs} role="tablist">
                        {dashFiles.map(f => {
                            const isActive = String(f.fileid) === String(activeFile?.fileid);
                            return (
                                <div
                                    key={f.fileid}
                                    role="tab"
                                    aria-selected={isActive}
                                    className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                                    title={f.name}
                                    onClick={() => selectTab(String(f.fileid))}
                                >
                                    <span className={styles.tabName}>{(f.name || "").replace(/\.(csv|json|xlsx?|pdf)$/i, "")}</span>
                                    {canEdit && (
                                        <span
                                            className={`${styles.tabRefresh} ${refreshingFid === String(f.fileid) ? styles.tabRefreshSpin : ""}`}
                                            title="Refresh linked data from the PO table"
                                            onClick={ev => { ev.stopPropagation(); if (refreshingFid === "") refreshTabData(f); }}
                                        >
                                            <FiRefreshCw size={12} />
                                        </span>
                                    )}
                                    {canEdit && onRemove && (
                                        <span
                                            className={styles.tabRemove}
                                            title="Remove from sidebar lists"
                                            onClick={ev => { ev.stopPropagation(); onRemove(f); }}
                                        >
                                            <FiX size={12} />
                                        </span>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.body}>
                        {isLoading ? (
                            <div className={styles.loading}>
                                <div className={styles.typingDots}><span></span><span></span><span></span></div>
                            </div>
                        ) : activeFile ? (
                            canEditLinked ? (
                                <JsonListTableEditable_dash
                                    key={activeFile.fileid}
                                    data={data}
                                    title={hidePdfExt_dash(activeFile.name)}
                                    fid={String(activeFile.fileid)}
                                    gid={selectedGroup?.selectionId || ""}
                                    loggedInUser={loggedInUser}
                                    ext_stamp={toDate_dash(activeFile.ext_stamp) || new Date(0)}
                                    onPersisted={onLinkedDataPersisted}
                                />
                            ) : (
                                <JsonListTable_dash
                                    key={activeFile.fileid}
                                    data={data}
                                    title={hidePdfExt_dash(activeFile.name)}
                                    fid={String(activeFile.fileid)}
                                    storageKey={filtersKey_dash(loggedInUser?.userId, oid, activeFile.fileid)}
                                    ext_stamp={toDate_dash(activeFile.ext_stamp) || new Date(0)}
                                />
                            )
                        ) : null}
                    </div>

                    <div className={styles.resizer} onMouseDown={onResizeStart} title="Drag to resize" />
                </div>
            )}
        </div>
    );
};

export default DashListSidebar_dash;
